import React, { forwardRef, useEffect, useRef, useState } from "react";
import { Icons } from "@/assets";
import { cx } from "@/utils/cx";
import { containsOnlyDigits } from "@/utils/validation";

interface CountryCodeOption {
    label: string;
    value: string;
}

interface MobileNumberFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "onBlur" | "value"> {
    name: string;
    label: string;
    value?: string;
    countryCodeValue?: string;
    countryCodeOptions?: CountryCodeOption[];
    onChange?: (val: string) => void;
    onBlur?: (val: string) => void;
    onCountryCodeChange?: (val: string) => void;
    error?: string;
    required?: boolean;
}

const DEFAULT_COUNTRY_CODES: CountryCodeOption[] = [
    { label: "+91", value: "+91" },
    // { label: "+1", value: "+1" },
    // { label: "+44", value: "+44" },
    // { label: "+971", value: "+971" },
];

export const MobileNumberField = forwardRef<HTMLInputElement, MobileNumberFieldProps>(({
    name,
    label,
    value,
    countryCodeValue = "+91",
    countryCodeOptions = DEFAULT_COUNTRY_CODES,
    onChange,
    onBlur,
    onCountryCodeChange,
    error,
    required,
    disabled,
    className,
    placeholder,
    ...props
}, ref) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [isCountryCodeOpen, setIsCountryCodeOpen] = useState(false);
    const [localError, setLocalError] = useState("");

    const selectedLabel = countryCodeOptions.find((option) => option.value === countryCodeValue)?.label || countryCodeValue;
    const resolvedError = error || localError;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsCountryCodeOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleValueChange = (nextValue: string) => {
        if (!nextValue) {
            setLocalError("");
            onChange?.("");
            return;
        }

        if (!containsOnlyDigits(nextValue)) {
            setLocalError("Enter number only");
            return;
        }

        setLocalError("");
        onChange?.(nextValue);
    };

    return (
        <div ref={wrapperRef} className="flex flex-col gap-2.5 w-full relative">
            <label htmlFor={name} className="text-base text-gray-90">
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
            </label>

            <div
                className={cx(
                    "relative w-full rounded-[10px] p-[1.5px] transition-all duration-200 ease-in-out group",
                    resolvedError
                        ? "bg-[#ff424f]"
                        : "bg-gray-30 focus-within:bg-gradient-to-r focus-within:from-[#00B4EA] focus-within:to-[#8A00FF]",
                    disabled && "opacity-60 cursor-not-allowed bg-gray-200"
                )}
            >
                <div className="relative flex items-center bg-white rounded-[8.5px] overflow-visible">
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={() => setIsCountryCodeOpen((open) => !open)}
                        className={cx(
                            "h-[51px] min-w-[86px] px-4 shrink-0",
                            "flex items-center justify-center gap-2 border-r border-gray-30 bg-transparent",
                            "text-base text-gray-90 outline-none transition-colors",
                            disabled ? "cursor-not-allowed" : "hover:text-gray-110"
                        )}
                    >
                        <span className="truncate">{selectedLabel}</span>
                        <Icons.ChevronDownIcon className={cx("w-4 h-4 shrink-0 text-gray-50 transition-transform", isCountryCodeOpen && "rotate-180")} />
                    </button>

                    <input
                        ref={ref}
                        id={name}
                        name={name}
                        type="text"
                        inputMode="numeric"
                        value={value || ""}
                        disabled={disabled}
                        placeholder={placeholder}
                        onChange={(event) => handleValueChange(event.target.value)}
                        onBlur={(event) => onBlur?.(event.target.value)}
                        className={cx(
                            "w-full h-[51px] pl-5 pr-10 text-black text-sm placeholder:text-gray-60 outline-none bg-transparent",
                            disabled && "cursor-not-allowed bg-gray-50",
                            className
                        )}
                        {...props}
                    />

                    {resolvedError && (
                        <div className="absolute right-4 pointer-events-none flex items-center">
                            <Icons.ErrorIcon className="w-5 h-5 text-[#ff424f]" />
                        </div>
                    )}

                    {isCountryCodeOpen && !disabled && (
                        <div className="absolute top-[56px] left-0 min-w-[112px] bg-white border border-gray-200 rounded-[8px] shadow-lg max-h-[200px] overflow-y-auto z-50">
                            {countryCodeOptions.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => {
                                        onCountryCodeChange?.(option.value);
                                        setIsCountryCodeOpen(false);
                                    }}
                                    className={cx(
                                        "w-full px-3 py-2 text-left text-sm hover:bg-gray-50",
                                        option.value === countryCodeValue ? "text-[#0a68ff] font-medium" : "text-gray-700"
                                    )}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {resolvedError && (
                <p className="text-xs text-error-60 font-medium">{resolvedError}</p>
            )}
        </div>
    );
});

MobileNumberField.displayName = "MobileNumberField";
